import React, { useLayoutEffect, useEffect, useState } from 'react';
import { Card, Row, Col, Table, Spinner, Form } from 'react-bootstrap';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { getStudents } from '../../service/apiService';
import './StaffHome.css';

function StaffHome() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('monthly'); // ✅ new filter state

  // Fetch students from API
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const data = await getStudents();
        const sorted = data.sort(
          (a, b) => new Date(b.admission_date) - new Date(a.admission_date)
        );
        setStudents(sorted);
      } catch (error) {
        console.error('Error fetching students:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, []);

  // ✅ Data aggregation logic
  const aggregateData = (type) => {
    const map = new Map();
    const now = new Date();

    students.forEach((student) => {
      const date = new Date(student.admission_date);
      if (isNaN(date)) return;

      let key;

      switch (type) {
        case 'daily':
          key = date.toLocaleDateString('default', { day: 'numeric', month: 'short' });
          break;
        case 'weekly': {
          const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
          const week = Math.ceil(((date - firstDayOfYear) / 86400000 + firstDayOfYear.getDay() + 1) / 7);
          key = `W${week} ${date.getFullYear()}`;
          break;
        }
        case 'yearly':
          key = date.getFullYear().toString();
          break;
        default:
          key = date.toLocaleString('default', { month: 'short', year: '2-digit' });
      }

      map.set(key, (map.get(key) || 0) + 1);
    });

    return Array.from(map, ([key, value]) => ({ category: key, count: value }));
  };

  const chartData = aggregateData(filter);

  // ✅ Chart setup
  useLayoutEffect(() => {
    if (!chartData.length) return;

    let root = am5.Root.new('admissionChart');
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: false,
        wheelX: 'panX',
        wheelY: 'zoomX',
        layout: root.verticalLayout,
      })
    );

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'category',
        renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 30 }),
      })
    );
    xAxis.data.setAll(chartData);

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererY.new(root, {}) })
    );

    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Admissions',
        xAxis,
        yAxis,
        valueYField: 'count',
        categoryXField: 'category',
        tooltip: am5.Tooltip.new(root, { labelText: '{categoryX}: {valueY}' }),
      })
    );

    series.columns.template.setAll({
      cornerRadiusTL: 4,
      cornerRadiusTR: 4,
      fillOpacity: 0.9,
    });

    series.data.setAll(chartData);
    series.appear(1000);
    chart.appear(1000, 100);

    return () => root.dispose();
  }, [chartData]);

  return (
    <div className="staff-dashboard">
      <h3 className="mb-4">Staff Dashboard</h3>

      {/* Top Stats */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="shadow-sm stat-card">
            <Card.Body>
              <h6>Total Admissions</h6>
              <h3 className="fw-bold text-primary">{students.length}</h3>
              <p className="text-muted small mb-0">All-time total</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm stat-card">
            <Card.Body>
              <h6>Today’s Admissions</h6>
              <h3 className="fw-bold text-success">
                {
                  students.filter(
                    (s) =>
                      new Date(s.admission_date).toDateString() ===
                      new Date().toDateString()
                  ).length
                }
              </h3>
              <p className="text-muted small mb-0">New students today</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm stat-card">
            <Card.Body>
              <h6>Active Courses</h6>
              <h3 className="fw-bold text-warning">
                {new Set(students.map((s) => s.course)).size}
              </h3>
              <p className="text-muted small mb-0">Running batches</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Chart Section */}
      <Card className="shadow-sm p-3 mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Admissions Overview</h5>

          {/* ✅ Filter Dropdown */}
          <Form.Select
            size="sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ width: '160px' }}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </Form.Select>
        </div>

        {chartData.length ? (
          <div id="admissionChart" style={{ width: '100%', height: '300px' }}></div>
        ) : (
          <p className="text-muted text-center mt-3">
            No data available for chart
          </p>
        )}
      </Card>

      {/* Recent Students */}
      <Card className="shadow-sm p-4">
        <h5 className="mb-3">Recent Admissions</h5>

        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : students.length === 0 ? (
          <p className="text-muted">No student admissions found.</p>
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Student Name</th>
                <th>Course</th>
                <th>Date</th>
                <th>Phone</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {students.slice(0, 10).map((student, index) => (
                <tr key={student.id || index}>
                  <td>{index + 1}</td>
                  <td>{student.name}</td>
                  <td>{student.course}</td>
                  <td>{student.admission_date}</td>
                  <td>{student.phone || '-'}</td>
                  <td>{student.email || '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

export default StaffHome;
